import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useAlertContext, useClinicContext, useUserContext } from "../contexts";
import { AlertType, ApplicationScreen, Clinic, ClinicSetting } from "../models";
import {
	ADMIN,
	EXECUTIVE_CENTRE_ADMIN,
	CENTRE_ADMIN,
	apiAccessRequest,
	SETTING_QUEUE_ENABLE_TELEHEALTH,
	SETTING_OM_TELEHEALTH_PROMPT_TIME,
	SETTING_OM_ENABLE_TELEHEALTH
} from "../constants";
import { BuildUrl } from "../helpers/ApiHelpers";
import {
	DAYS_ORDER,
	GlobalHoursTable,
	TelehealthSchedulePrompt,
	LocalHoursTable,
	TelehealthSchedule,
	Settings,
	TelehealthType,
	ScheduleRangeValidationResult
} from "../components/TelehealthSchedule";
import { SelectControl } from "../components/Common";
import {
	buildInvalidScheduleMessage,
	buildMissingOrInvalidTimeMessage,
	hasInvalidTimeRange,
	hasMissingOrInvalidTime,
	toMinutes
} from "../components/TelehealthSchedule/Validations";
import { useCreateAuditLog } from "../hooks";

const LHamsClinicId = 269;
const AHamsClinicId = 2115;
const MPamsClinicId = 266;
const CNamsClinicId = 270;

export const TelehealthAvailability = () => {
	const { clinics } = useClinicContext();
	const { showAlert } = useAlertContext();
	const { user } = useUserContext();
	const { instance, accounts } = useMsal();
	const account = useMemo(() => accounts[0], [accounts]);
	const { createAuditLog } = useCreateAuditLog();

	const [selectedClinicId, setSelectedClinicId] = useState<number>(0);
	const [clinicSettings, setClinicSettings] = useState<ClinicSetting[]>();

	const [telehealthPromptTime, setTelehealthPromptTime] = useState(0);
	const [enableOMAppTelehealth, setEnableOMAppTelehealth] = useState(false);
	const [enableReceptionAppTelehealth, setEnableReceptionAppTelehealth] =
		useState(false);

	const [prompts, setPrompts] = useState<TelehealthSchedulePrompt[]>([]);
	const [globalSchedules, setGlobalSchedules] = useState<TelehealthSchedule[]>(
		[]
	);
	const [localSchedules, setLocalSchedules] = useState<TelehealthSchedule[]>(
		[]
	);
	const [originalTelehealthSchedules, setOriginalTelehealthSchedules] =
		useState<TelehealthSchedule[]>([]);

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingError, setIsLoadingError] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [saveValidation, setSaveValidation] =
		useState<ScheduleRangeValidationResult | null>(null);
	const nextTempId = useRef(-1);
	const [filteredClinics, setFilteredClnics] = useState<Clinic[]>([]);

	useEffect(() => {
		if (!clinics || clinics.length === 0 || !user) {
			return;
		}
		const filteredClinics: Clinic[] = [];
		const testClinic = clinics?.find((clinic) => 7950 === clinic.amsId);
		if (testClinic != null) {
			filteredClinics.push(testClinic);
		}
		if (
			user.roles.includes(ADMIN) ||
			user.roles.includes(CENTRE_ADMIN) ||
			user.roles.includes(EXECUTIVE_CENTRE_ADMIN) ||
			user.roles.includes("centre_admin_ashmore")
		) {
			const ahClinic = clinics?.find(
				(clinic) => AHamsClinicId === clinic.amsId
			);
			if (ahClinic) {
				filteredClinics.push(ahClinic);
			}
		}
		if (
			user.roles.includes(ADMIN) ||
			user.roles.includes(CENTRE_ADMIN) ||
			user.roles.includes(EXECUTIVE_CENTRE_ADMIN) ||
			user.roles.includes("centre_admin_loganholme")
		) {
			const lhClinic = clinics?.find(
				(clinic) => LHamsClinicId === clinic.amsId
			);
			if (lhClinic) {
				filteredClinics.push(lhClinic);
			}
		}
		if (
			user.roles.includes(ADMIN) ||
			user.roles.includes(CENTRE_ADMIN) ||
			user.roles.includes(EXECUTIVE_CENTRE_ADMIN)
		) {
			const mpClinic = clinics?.find(
				(clinic) => MPamsClinicId === clinic.amsId
			);
			if (mpClinic) {
				filteredClinics.push(mpClinic);
			}
			const cnClinic = clinics?.find(
				(clinic) => CNamsClinicId === clinic.amsId
			);
			if (cnClinic) {
				filteredClinics.push(cnClinic);
			}
		}
		setFilteredClnics(filteredClinics);
	}, [clinics, user]);

	const handleAddSchedule = useCallback(
		(day: string, telehealthType: TelehealthType) => {
			setGlobalSchedules((prev) => [
				...prev,
				{
					id: nextTempId.current--,
					clinicId: Number(selectedClinicId),
					dayOfWeek: day,
					startTime: "",
					endTime: "",
					telehealthType,
					active: true
				}
			]);
		},
		[selectedClinicId]
	);

	const handleRemoveSchedule = useCallback(
		(id: number) => {
			const removedSchedule = globalSchedules.find(
				(schedule) => schedule.id === id
			);

			if (removedSchedule != null) {
				setGlobalSchedules((prev) => prev.filter((s) => s.id !== id));
				if (
					globalSchedules.find(
						(schedule) =>
							schedule.id !== id &&
							schedule.dayOfWeek === removedSchedule.dayOfWeek
					) == null
				) {
					setPrompts((prev) =>
						prev.filter(
							(prompt) => prompt.dayOfWeek !== removedSchedule.dayOfWeek
						)
					);
				}
			} else if (localSchedules.some((schedule) => schedule.id === id)) {
				setLocalSchedules((prev) => prev.filter((s) => s.id !== id));
			}
		},
		[globalSchedules, localSchedules]
	);

	const handleScheduleChange = useCallback(
		(id: number, field: "startTime" | "endTime", value: string) => {
			setGlobalSchedules((prev) =>
				prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
			);
		},
		[]
	);

	const handlePromptChange = useCallback(
		(
			prompt: TelehealthSchedulePrompt,
			field: "allowTelehealth" | "waitTimePrompt" | "cancellationPrompt",
			value: boolean
		) => {
			if (prompt.id > 0) {
				setPrompts((prev) =>
					prev.map((p) => (p.id === prompt.id ? { ...p, [field]: value } : p))
				);
			} else {
				const existing = prompts.find((p) => p.dayOfWeek === prompt.dayOfWeek);
				if (existing) {
					setPrompts((prev) =>
						prev.map((p) =>
							p.dayOfWeek === prompt.dayOfWeek ? { ...p, [field]: value } : p
						)
					);
				} else {
					const newPrompts = [...prompts];
					newPrompts.push({ ...prompt, [field]: value });
					setPrompts(newPrompts);
				}
			}
		},
		[prompts]
	);

	const handleLocalScheduleChange = useCallback(
		(
			schedule: TelehealthSchedule,
			field: "startTime" | "endTime" | "active",
			value: string
		) => {
			const convertedValue = field === "active" ? value === "true" : value;
			if (schedule.id > 0) {
				setLocalSchedules((prev) =>
					prev.map((p) =>
						p.id === schedule.id ? { ...p, [field]: convertedValue } : p
					)
				);
			} else {
				const existing = localSchedules.find(
					(s) =>
						s.dayOfWeek === schedule.dayOfWeek &&
						s.telehealthType === schedule.telehealthType
				);
				if (existing) {
					setLocalSchedules((prev) =>
						prev.map((s) =>
							s.dayOfWeek === schedule.dayOfWeek &&
							s.telehealthType === schedule.telehealthType
								? { ...s, [field]: convertedValue }
								: s
						)
					);
				} else {
					const newSchedules = [...localSchedules];
					newSchedules.push({
						...schedule,
						[field]: convertedValue
					});
					setLocalSchedules(newSchedules);
				}
			}
		},
		[localSchedules]
	);

	const fetchTelehealthSchedules = useCallback(
		async (clinicId: number) => {
			try {
				setIsLoading(true);
				setIsLoadingError(false);
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const response = await fetch(
					BuildUrl(`TelehealthSchedule/clinic/${clinicId}`),
					{ headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
				);
				if (!response.ok) throw new Error();
				const data: TelehealthSchedule[] = await response.json();
				setOriginalTelehealthSchedules(data);

				setGlobalSchedules(
					data.filter((s) => s.telehealthType === TelehealthType.Global)
				);
				const prompts = data
					.filter((s) => s.prompts != null)
					.flatMap((s) => s.prompts ?? []);

				setPrompts(
					Array.from(
						prompts
							.reduce((acc, item) => {
								// If the id isn't in the Map, add it
								if (item.id && !acc.has(item.id)) {
									acc.set(item.id, item);
								}
								return acc;
							}, new Map())
							.values()
					)
				);

				setLocalSchedules(
					data.filter((s) => s.telehealthType === TelehealthType.Local)
				);
			} catch {
				setIsLoadingError(true);
			} finally {
				setIsLoading(false);
			}
		},
		[account, instance]
	);

	const getBooleanSetting = (
		settingName: string,
		settings: ClinicSetting[]
	): boolean => {
		if (!settings) {
			return false;
		}
		return (
			(settings.find((s) => s.settingName === settingName)?.value ?? "0") ===
			"1"
		);
	};

	const getNumberSetting = (
		settingName: string,
		settings: ClinicSetting[]
	): number => {
		if (!settings) {
			return 0;
		}
		const value =
			settings.find((s) => s.settingName === settingName)?.value ?? "0";
		return Number(value);
	};

	const getClinicSetting = (
		settingName: string,
		settings: ClinicSetting[]
	): ClinicSetting => {
		return (
			settings.find((s) => s.settingName === settingName) ??
			({} as ClinicSetting)
		);
	};

	const fetchClinicSettings = useCallback(async (clinicId: number) => {
		try {
			setIsLoading(true);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const response = await fetch(
				BuildUrl(`Clinic/ClinicSettings?id=${clinicId}`),
				{ headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
			);
			if (!response.ok) throw new Error();
			const settings: ClinicSetting[] = await response.json();
			setClinicSettings(settings);
			const telehealthPromptTime = getNumberSetting(
				SETTING_OM_TELEHEALTH_PROMPT_TIME,
				settings
			);
			setTelehealthPromptTime(telehealthPromptTime);
			const enableReceptionTelehealth = getBooleanSetting(
				SETTING_QUEUE_ENABLE_TELEHEALTH,
				settings
			);
			setEnableReceptionAppTelehealth(enableReceptionTelehealth);
			const enableOMAppTelehealth = getBooleanSetting(
				SETTING_OM_ENABLE_TELEHEALTH,
				settings
			);
			setEnableOMAppTelehealth(enableOMAppTelehealth);
		} catch {
			setIsLoadingError(true);
		} finally {
			setIsLoading(false);
		}
	}, [account, instance]);

	useEffect(() => {
		if (selectedClinicId > 0) {
			fetchTelehealthSchedules(selectedClinicId);
			fetchClinicSettings(selectedClinicId);
		}
	}, [fetchClinicSettings, fetchTelehealthSchedules, selectedClinicId]);

	const handleClinicChange = (value?: string) => {
		setSelectedClinicId(Number(value));
		setSaveValidation(null);
	};

	const { sessionOverlapDays, localOverlapDays } = useMemo(() => {
		const overlaps = (s1: number, e1: number, s2: number, e2: number) =>
			s1 < e2 && s2 < e1;

		const sessionOverlapDays = new Set<string>();
		const localOverlapDays = new Set<string>();

		for (const day of DAYS_ORDER) {
			const daySlots = globalSchedules.filter((s) => s.dayOfWeek === day);
			for (let i = 0; i < daySlots.length; i++) {
				for (let j = i + 1; j < daySlots.length; j++) {
					const aStart = toMinutes(daySlots[i].startTime);
					const aEnd = toMinutes(daySlots[i].endTime);
					const bStart = toMinutes(daySlots[j].startTime);
					const bEnd = toMinutes(daySlots[j].endTime);
					if (
						aStart !== null &&
						aEnd !== null &&
						bStart !== null &&
						bEnd !== null &&
						overlaps(aStart, aEnd, bStart, bEnd)
					) {
						sessionOverlapDays.add(day);
					}
				}
			}
		}

		for (const local of localSchedules) {
			const lStart = toMinutes(local.startTime);
			const lEnd = toMinutes(local.endTime);
			if (lStart === null || lEnd === null) {
				continue;
			}
			const daySlots = globalSchedules.filter(
				(s) => s.dayOfWeek === local.dayOfWeek
			);
			for (const slot of daySlots) {
				const sStart = toMinutes(slot.startTime);
				const sEnd = toMinutes(slot.endTime);
				if (
					sStart !== null &&
					sEnd !== null &&
					overlaps(lStart, lEnd, sStart, sEnd)
				) {
					localOverlapDays.add(local.dayOfWeek);
					break;
				}
			}
		}

		return { sessionOverlapDays, localOverlapDays };
	}, [globalSchedules, localSchedules]);

	const getScheduleRangeValidation =
		useCallback((): ScheduleRangeValidationResult => {
			const invalidGlobalFormatSchedules = globalSchedules.filter(
				hasMissingOrInvalidTime
			);
			const invalidLocalFormatSchedules = localSchedules.filter(
				hasMissingOrInvalidTime
			);
			const invalidGlobalSchedules =
				globalSchedules.filter(hasInvalidTimeRange);
			const invalidLocalSchedules = localSchedules.filter(hasInvalidTimeRange);
			const invalidGlobalScheduleIds = new Set(
				invalidGlobalSchedules
					.concat(invalidGlobalFormatSchedules)
					.map((schedule) => schedule.id)
			);
			const invalidLocalScheduleDays = new Set(
				invalidLocalSchedules
					.concat(invalidLocalFormatSchedules)
					.map((schedule) => schedule.dayOfWeek)
			);
			const globalMessages = [
				buildMissingOrInvalidTimeMessage(invalidGlobalFormatSchedules),
				buildInvalidScheduleMessage(
					invalidGlobalSchedules,
					"Telehealth schedule end time"
				)
			].filter(Boolean);
			const localMessages = [
				buildMissingOrInvalidTimeMessage(invalidLocalFormatSchedules),
				buildInvalidScheduleMessage(
					invalidLocalSchedules,
					"Telehealth schedule end time"
				)
			].filter(Boolean);

			return {
				hasErrors:
					invalidGlobalScheduleIds.size > 0 ||
					invalidLocalScheduleDays.size > 0,
				invalidGlobalScheduleIds,
				invalidLocalScheduleDays,
				globalMessage: globalMessages.join(" "),
				localMessage: localMessages.join(" ")
			};
		}, [globalSchedules, localSchedules]);

	const handleSave = async () => {
		if (selectedClinicId === 0) {
			return;
		}
		const scheduleRangeValidation = getScheduleRangeValidation();
		setSaveValidation(scheduleRangeValidation);
		if (scheduleRangeValidation.hasErrors) {
			showAlert({
				message: "Fix telehealth schedule errors before saving",
				alertType: AlertType.error
			});
			return;
		}
		setIsSaving(true);
		try {
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const authHeader = {
				Authorization: `Bearer ${tokenResponse.accessToken}`
			};

			const combinedSchedules = [...globalSchedules.concat(localSchedules)];
			const newSchedules = combinedSchedules.filter(
				(schedule) => schedule.id <= 0
			);
			const deletedSchedules = originalTelehealthSchedules.filter(
				(schedule) =>
					schedule.id > 0 &&
					!combinedSchedules.find((s) => s.id === schedule.id)
			);
			const updatedSchedules = combinedSchedules.filter(
				(schedule) =>
					originalTelehealthSchedules.find(
						(s) =>
							s.id === schedule.id &&
							(s.startTime !== schedule.startTime ||
								s.endTime !== schedule.endTime ||
								s.active !== schedule.active)
					) != null
			);
			const originalPrompts = originalTelehealthSchedules
				.filter((schedule) => schedule.prompts != null)
				.flatMap((schedule) => schedule.prompts ?? []);
			let newPrompts = prompts.filter((h) => h.id <= 0);
			const newPromptsFromNewSchedules = globalSchedules
				.filter(
					(schedule) =>
						!prompts.some((prompt) => schedule.dayOfWeek === prompt.dayOfWeek)
				)
				.map((schedule) => ({
					id: 0,
					clinicId: selectedClinicId,
					dayOfWeek: schedule.dayOfWeek,
					allowTelehealth: false,
					waitTimePrompt: false,
					cancellationPrompt: false,
					telehealthType: TelehealthType.Global
				}));

			if (newPromptsFromNewSchedules.length > 0) {
				newPrompts = [...newPrompts, ...newPromptsFromNewSchedules];
			}
			const deletedPrompts = originalPrompts.filter(
				(prompt) => prompt.id > 0 && !prompts.find((s) => s.id === prompt.id)
			);
			const updatedPrompts = prompts.filter(
				(prompt) =>
					originalPrompts.find(
						(p) =>
							p.id === prompt.id &&
							(p.allowTelehealth !== prompt.allowTelehealth ||
								p.waitTimePrompt !== prompt.waitTimePrompt ||
								p.cancellationPrompt !== prompt.cancellationPrompt)
					) != null
			);

			let scheduleChangesExist = false;
			if (deletedSchedules.length > 0) {
				const body = deletedSchedules.map((schedule) => schedule.id);
				const response = await fetch(BuildUrl("TelehealthSchedule"), {
					method: "DELETE",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(body)
				});
				if (!response.ok) throw new Error();
				scheduleChangesExist = true;
			}

			if (updatedSchedules.length > 0) {
				const response = await fetch(BuildUrl("TelehealthSchedule"), {
					method: "PATCH",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(updatedSchedules)
				});
				if (!response.ok) throw new Error();
				scheduleChangesExist = true;
			}

			if (newSchedules.length > 0) {
				const response = await fetch(BuildUrl("TelehealthSchedule"), {
					method: "POST",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(newSchedules)
				});
				if (!response.ok) throw new Error();
				scheduleChangesExist = true;
			}

			if (deletedPrompts.length > 0) {
				const body = deletedPrompts.map((prompt) => prompt.id);
				const response = await fetch(BuildUrl("TelehealthSchedule/prompt"), {
					method: "DELETE",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(body)
				});
				if (!response.ok) throw new Error();
				scheduleChangesExist = true;
			}

			if (updatedPrompts.length > 0) {
				const response = await fetch(BuildUrl("TelehealthSchedule/prompt"), {
					method: "PATCH",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(updatedPrompts)
				});
				if (!response.ok) throw new Error();
				scheduleChangesExist = true;
			}

			if (newPrompts.length > 0) {
				const response = await fetch(BuildUrl("TelehealthSchedule/prompt"), {
					method: "POST",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(newPrompts)
				});
				if (!response.ok) throw new Error();
				scheduleChangesExist = true;
			}

			const updatedClinicSettings = [];

			const originalTelehealthPromptTime = getNumberSetting(
				SETTING_OM_TELEHEALTH_PROMPT_TIME,
				clinicSettings ?? []
			);
			const originalEnableReceptionTelehealth = getBooleanSetting(
				SETTING_QUEUE_ENABLE_TELEHEALTH,
				clinicSettings ?? []
			);
			const originalEnableOMAppTelehealth = getBooleanSetting(
				SETTING_OM_ENABLE_TELEHEALTH,
				clinicSettings ?? []
			);

			let enableReceptionTelehealthMessage = "";
			if (originalEnableReceptionTelehealth !== enableReceptionAppTelehealth) {
				const setting = getClinicSetting(
					SETTING_QUEUE_ENABLE_TELEHEALTH,
					clinicSettings ?? []
				);
				updatedClinicSettings.push({
					setting,
					newValue: enableReceptionAppTelehealth ? "1" : "0"
				});
				enableReceptionTelehealthMessage = `Reception app telehealth ${
					originalEnableReceptionTelehealth ? "is disabled" : " is enabled"
				}.<br/>`;
			}

			let enableOMAppTelehealthMessage = "";
			if (originalEnableOMAppTelehealth !== enableOMAppTelehealth) {
				const setting = getClinicSetting(
					SETTING_OM_ENABLE_TELEHEALTH,
					clinicSettings ?? []
				);
				updatedClinicSettings.push({
					setting,
					newValue: enableOMAppTelehealth ? "1" : "0"
				});
				enableOMAppTelehealthMessage = `Our Medical app telehealth ${
					originalEnableOMAppTelehealth ? "is disabled" : " is enabled"
				}.<br/>`;
			}

			let enableTelehealthPromptTimeMessage = "";
			if (originalTelehealthPromptTime !== telehealthPromptTime) {
				const setting = getClinicSetting(
					SETTING_OM_TELEHEALTH_PROMPT_TIME,
					clinicSettings ?? []
				);
				if (setting) {
					updatedClinicSettings.push({
						setting,
						newValue: telehealthPromptTime.toString()
					});
				}
				enableTelehealthPromptTimeMessage = `Patient prompt time is set to ${
					telehealthPromptTime
				} mins.<br/>`;
			}

			if (updatedClinicSettings.length > 0) {
				const body = updatedClinicSettings.map((update) => ({
					id: update.setting.id,
					clinicId: selectedClinicId,
					settingId: update.setting.settingId,
					value: update.newValue,
					username: user?.username
				}));
				const response = await fetch(BuildUrl("Clinic/ClinicSettings"), {
					method: "PUT",
					headers: { "Content-Type": "application/json", ...authHeader },
					body: JSON.stringify(body)
				});
				if (!response.ok) throw new Error();
				await callCreateAuditLog(updatedClinicSettings);
			}

			showAlert({
				message:
					`${enableReceptionTelehealthMessage} ${enableOMAppTelehealthMessage} ${enableTelehealthPromptTimeMessage} ${
						scheduleChangesExist ? "Telehealth Schedules saved" : ""
					}`
						.trim()
						.replace("  ", " "),
				alertType: AlertType.success
			});
			setSaveValidation(null);
			await fetchTelehealthSchedules(selectedClinicId);
			await fetchClinicSettings(selectedClinicId);
		} catch {
			showAlert({
				message: "Failed to save Telehealth Schedules",
				alertType: AlertType.error
			});
		} finally {
			setIsSaving(false);
		}
	};

	const callCreateAuditLog = async (updates: any[]) => {
		try {
			await createAuditLog(
				updates.map((update) => ({
					clinicId: selectedClinicId,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminTelehealthScheule,
					action: `Updated clinic setting ${update.setting.settingName}`,
					fromValue: update.setting.value,
					toValue: update.newValue
				}))
			);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const handleCancel = async () => {
		showAlert({
			message: "Cancelled Telehealth Schedule changes",
			alertType: AlertType.info,
			timeout: true
		});
		await fetchTelehealthSchedules(selectedClinicId);
		await fetchClinicSettings(selectedClinicId);
	};

	return (
		<>
			<div className="ch-l-text my-4">Telehealth Schedule</div>
			<div className="mb-6" style={{ width: 300 }}>
				<SelectControl
					id="clinicId"
					label="Centre"
					value={selectedClinicId}
					options={[
						{ value: "0", label: "" },
						...(filteredClinics ?? []).map((clinic) => ({
							value: clinic.id,
							label: clinic.name
						}))
					]}
					includeNone={false}
					handleChange={handleClinicChange}
				/>
			</div>

			{(isLoading || isSaving) && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}

			{isLoadingError && (
				<div className="ch-m-text ch-error-text mb-3">
					Failed to load telehealth schedules
				</div>
			)}

			{selectedClinicId > 0 && !isLoading && (
				<>
					<Settings
						telehealthPromptTime={telehealthPromptTime}
						setTelehealthPromptTime={setTelehealthPromptTime}
						enablOMAppTelehealth={enableOMAppTelehealth}
						setEnableOMAppTelehealth={setEnableOMAppTelehealth}
						enableReceptionAppTelehealth={enableReceptionAppTelehealth}
						setEnableReceptionAppTelehealth={setEnableReceptionAppTelehealth}
					/>

					<div className="ch-m-text mb-6">Global Telehealth</div>
					<GlobalHoursTable
						clinicId={selectedClinicId}
						schedules={globalSchedules}
						prompts={prompts}
						invalidScheduleIds={
							saveValidation?.invalidGlobalScheduleIds ?? new Set<number>()
						}
						validationMessage={saveValidation?.globalMessage ?? null}
						localOverlapDays={localOverlapDays}
						onAddSchedule={handleAddSchedule}
						onRemoveSchedule={handleRemoveSchedule}
						onScheduleChange={handleScheduleChange}
						onPromptChange={handlePromptChange}
					/>

					<div className="ch-m-text mb-6">Local Telehealth</div>
					<LocalHoursTable
						clinicId={selectedClinicId}
						schedules={localSchedules}
						invalidScheduleDays={
							saveValidation?.invalidLocalScheduleDays ?? new Set<string>()
						}
						validationMessage={saveValidation?.localMessage ?? null}
						localOverlapDays={localOverlapDays}
						onScheduleChange={handleLocalScheduleChange}
						onRemoveSchedule={handleRemoveSchedule}
					/>

					<Box mt={4}>
						{sessionOverlapDays.size > 0 && (
							<Typography color="error" mb={1} fontSize="1rem">
								Session overlap within global hours on:{" "}
								<strong>{[...sessionOverlapDays].join(", ")}</strong>.
							</Typography>
						)}
						{localOverlapDays.size > 0 && (
							<Typography color="error" mb={1} fontSize="1rem">
								Local hours overlap with global hours on:{" "}
								<strong>{[...localOverlapDays].join(", ")}</strong>.
							</Typography>
						)}
						<div className="flex gap-4">
							<button
								className="ch-button ch-button-primary"
								onClick={(e) => handleCancel()}
							>
								Cancel
							</button>
							<button
								className="ch-button ch-button-primary"
								onClick={(e) => handleSave()}
								disabled={
									isSaving ||
									sessionOverlapDays.size > 0 ||
									localOverlapDays.size > 0
								}
							>
								{isSaving ? "Saving..." : "Save"}
							</button>
						</div>
					</Box>
				</>
			)}
		</>
	);
};
